import React from "react";
import {
  DemoResponsiveGrid,
  DemoSparkBarChart,
  DemoWorkbenchButton,
  DemoWorkbenchCard,
  DemoWorkbenchCopy,
  DemoWorkbenchInset,
  DemoWorkbenchMetricCard,
  DemoWorkbenchNotice,
  DemoWorkbenchStack,
} from "@csk/demo-ui";

interface MatrixImmunityConditions {
  C1_authority: boolean;
  C2_knowledge: boolean;
  C3_temporal: boolean;
  C4_resource: boolean;
}

interface MatrixImmunityBlock {
  name: string;
  status: "PASS" | "FAIL";
  margin: number;
}

interface MatrixImmunityResult {
  conditions: MatrixImmunityConditions;
  certification: string;
  nt_count: string;
  margin: number;
  blocks: MatrixImmunityBlock[];
  spectralSignature: number[];
  driftRate: number;
  robustness: string;
}

const MatrixImmunityPanel = (): React.JSX.Element => {
  const [result, setResult] = React.useState<MatrixImmunityResult | null>(null);

  const runVerification = (): void => {
    // Simulate MIT verification for the demo system
    const system: MatrixImmunityResult = {
      conditions: {
        C1_authority: true, // LEASE.demo is a closed subgroup
        C2_knowledge: true, // eigendecomposition from bridge matrix
        C3_temporal: true, // budget semigroup is stable
        C4_resource: true, // trace/det from footprint
      },
      certification: "CERTIFIED",
      nt_count: "10/10",
      margin: 0.847,
      blocks: [
        { name: "Authority (G1)", status: "PASS", margin: 0.95 },
        { name: "Knowledge (G2)", status: "PASS", margin: 0.88 },
        { name: "Temporal (G3)", status: "PASS", margin: 0.85 },
        { name: "Resource (G4)", status: "PASS", margin: 0.92 },
      ],
      spectralSignature: [0.95, 0.88, 0.72, 0.65, 0.51, 0.33, 0.21, 0.12],
      driftRate: 0.0,
      robustness: "HIGH",
    };
    setResult(system);
  };

  return (
    <DemoWorkbenchStack>
      <DemoWorkbenchCard
        title="Matrix Immunity Verification (§12.8)"
        actions={
          <DemoWorkbenchButton tone="accent" onClick={runVerification}>
            Run MIT Verification
          </DemoWorkbenchButton>
        }
      >
        <DemoWorkbenchCopy>
          Verifies all 10 Neutralization Theorems via 4 matrix conditions on
          GL(n). O(n³) verification using standard linear algebra.
        </DemoWorkbenchCopy>
      </DemoWorkbenchCard>

      {result && (
        <DemoWorkbenchStack>
          <DemoWorkbenchNotice
            tone={result.certification === "CERTIFIED" ? "success" : "danger"}
          >
            <div className="text-2xl font-bold">
              {result.certification === "CERTIFIED"
                ? "✓ CERTIFIED"
                : "✗ NOT CERTIFIED"}
            </div>
            <div className="text-gray-300">
              {result.nt_count} Neutralization Theorems satisfied
            </div>
            <div className="text-gray-400 text-sm">
              Overall margin: {result.margin.toFixed(3)} | Drift rate:{" "}
              {result.driftRate}% | Robustness: {result.robustness}
            </div>
          </DemoWorkbenchNotice>

          <DemoResponsiveGrid minColumnWidth={130} gap={8}>
            {result.blocks.map((block, i) => (
              <DemoWorkbenchMetricCard
                key={i}
                label={block.name}
                value={block.status}
                color={block.status === "PASS" ? "#34d399" : "#f87171"}
                sub={`margin: ${block.margin.toFixed(2)}`}
              />
            ))}
          </DemoResponsiveGrid>

          <DemoWorkbenchInset title="Spectral Governance Fingerprint">
            <DemoSparkBarChart
              ariaLabel="Sorted eigenvalues of the block-diagonal representation"
              bars={result.spectralSignature.map((value, index) => ({
                id: `lambda-${index}`,
                label: `λ${index + 1}`,
                value,
                color: "#14b8a6",
                title: `λ${index + 1} = ${value.toFixed(3)}`,
              }))}
              height={64}
              max={1}
              showLabels={false}
            />
            <DemoWorkbenchCopy size="xs" tone="muted" className="mt-1">
              Sorted eigenvalues of block-diagonal representation
            </DemoWorkbenchCopy>
          </DemoWorkbenchInset>

          <DemoWorkbenchInset className="text-sm text-gray-300">
            <span className="text-teal-400">exp(tr(X)) = det(exp(X))</span> —
            master identity verified. Resource accounting is consistent.
          </DemoWorkbenchInset>
        </DemoWorkbenchStack>
      )}
    </DemoWorkbenchStack>
  );
};

export default MatrixImmunityPanel;
